import {
  Box,
  Menu,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Divider,
} from "@mui/material";
import { isNil } from "lodash";
import React, { useState, useMemo, useCallback } from "react";

import { useToast } from "../../../contexts/ToastContext";
import {
  PatientsPaginationRequestDtoGenderEnum,
  PatientsPaginationRequestDtoStatusEnum,
} from "../../../libs/cellera-sdk/models";
import { useCelleraTheme } from "../../../theme/useCelleraTheme";
import FilterButton from "../../common/FilterButton";

interface PatientFiltersProps {
  gender: PatientsPaginationRequestDtoGenderEnum[];
  status: PatientsPaginationRequestDtoStatusEnum[];
  ageFrom: number | null;
  ageTo: number | null;
  onGenderChange: (gender: PatientsPaginationRequestDtoGenderEnum[]) => void;
  onStatusChange: (status: PatientsPaginationRequestDtoStatusEnum[]) => void;
  onAgeRangeChange: (ageFrom: number | null, ageTo: number | null) => void;
}

const PatientFilters: React.FC<PatientFiltersProps> = ({
  gender,
  status,
  ageFrom,
  ageTo,
  onGenderChange,
  onStatusChange,
  onAgeRangeChange,
}) => {
  const { colors } = useCelleraTheme();
  const { showError } = useToast();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [localAgeFrom, setLocalAgeFrom] = useState<string>(
    ageFrom?.toString() || ""
  );
  const [localAgeTo, setLocalAgeTo] = useState<string>(ageTo?.toString() || "");

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleGenderChange = useCallback(
    (genderValue: PatientsPaginationRequestDtoGenderEnum) => {
      const newGender = gender.includes(genderValue)
        ? gender.filter((g) => g !== genderValue)
        : [...gender, genderValue];
      onGenderChange(newGender);
    },
    [gender, onGenderChange]
  );

  const handleStatusChange = useCallback(
    (statusValue: PatientsPaginationRequestDtoStatusEnum) => {
      const newStatus = status.includes(statusValue)
        ? status.filter((s) => s !== statusValue)
        : [...status, statusValue];
      onStatusChange(newStatus);
    },
    [status, onStatusChange]
  );

  const handleAgeFromChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalAgeFrom(value);
      const numValue = value ? parseInt(value, 10) : null;

      if (numValue !== null && (isNaN(numValue) || numValue < 0)) {
        return;
      }

      if (numValue !== null && ageTo !== null && numValue > ageTo) {
        showError(
          "Invalid Age Range",
          `Age "From" (${numValue}) cannot be greater than age "To" (${ageTo}).`
        );
        return;
      }

      onAgeRangeChange(numValue, ageTo);
    },
    [ageTo, onAgeRangeChange, showError]
  );

  const handleAgeToChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalAgeTo(value);
      const numValue = value ? parseInt(value, 10) : null;

      if (numValue !== null && (isNaN(numValue) || numValue < 0)) {
        return;
      }

      if (numValue !== null && ageFrom !== null && numValue < ageFrom) {
        showError(
          "Invalid Age Range",
          `Age "To" (${numValue}) cannot be less than age "From" (${ageFrom}).`
        );
        return;
      }

      onAgeRangeChange(ageFrom, numValue);
    },
    [ageFrom, onAgeRangeChange, showError]
  );

  const handleClearAll = useCallback(() => {
    onGenderChange([]);
    onStatusChange([]);
    onAgeRangeChange(null, null);
    setLocalAgeFrom("");
    setLocalAgeTo("");
  }, [onGenderChange, onStatusChange, onAgeRangeChange]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (gender.length > 0) count++;
    if (status.length > 0) count++;
    if (ageFrom !== null || ageTo !== null) count++;
    return count;
  }, [gender.length, status.length, ageFrom, ageTo]);

  return (
    <Box className="flex items-center gap-2">
      <FilterButton
        activeFiltersCount={activeFiltersCount}
        onClick={handleClick}
      />

      <Menu
        anchorEl={anchorEl}
        open={!isNil(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              p: 2,
            },
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
            {activeFiltersCount > 0 && (
              <Typography
                variant="body2"
                onClick={handleClearAll}
                sx={{
                  color: colors.primary.main,
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Clear All
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Gender Filter */}
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mb: 1, color: colors.text.secondary }}
            >
              Gender
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={gender.includes(
                      PatientsPaginationRequestDtoGenderEnum.Male
                    )}
                    onChange={() =>
                      handleGenderChange(
                        PatientsPaginationRequestDtoGenderEnum.Male
                      )
                    }
                    size="small"
                  />
                }
                label="Male"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={gender.includes(
                      PatientsPaginationRequestDtoGenderEnum.Female
                    )}
                    onChange={() =>
                      handleGenderChange(
                        PatientsPaginationRequestDtoGenderEnum.Female
                      )
                    }
                    size="small"
                  />
                }
                label="Female"
              />
            </FormGroup>
          </Box>

          <Divider />

          {/* Age Range Filter */}
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mb: 1, color: colors.text.secondary }}
            >
              Age Range
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                size="small"
                type="number"
                placeholder="From"
                value={localAgeFrom}
                onChange={handleAgeFromChange}
                slotProps={{
                  htmlInput: {
                    min: 0,
                    max: ageTo !== null ? ageTo : 110,
                  },
                }}
                sx={{ flex: 1 }}
              />
              <Typography variant="body2">-</Typography>
              <TextField
                size="small"
                type="number"
                placeholder="To"
                value={localAgeTo}
                onChange={handleAgeToChange}
                slotProps={{
                  htmlInput: {
                    min: ageFrom !== null ? ageFrom : 0,
                    max: 110,
                  },
                }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>

          <Divider />

          {/* Status Filter */}
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mb: 1, color: colors.text.secondary }}
            >
              Status
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={status.includes(
                      PatientsPaginationRequestDtoStatusEnum.ActiveSharing
                    )}
                    onChange={() =>
                      handleStatusChange(
                        PatientsPaginationRequestDtoStatusEnum.ActiveSharing
                      )
                    }
                    size="small"
                  />
                }
                label="Active Sharing"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={status.includes(
                      PatientsPaginationRequestDtoStatusEnum.Revoked
                    )}
                    onChange={() =>
                      handleStatusChange(
                        PatientsPaginationRequestDtoStatusEnum.Revoked
                      )
                    }
                    size="small"
                  />
                }
                label="Revoked"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={status.includes(
                      PatientsPaginationRequestDtoStatusEnum.Requesting
                    )}
                    onChange={() =>
                      handleStatusChange(
                        PatientsPaginationRequestDtoStatusEnum.Requesting
                      )
                    }
                    size="small"
                  />
                }
                label="Requesting"
              />
            </FormGroup>
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

export default PatientFilters;
